import React, { useCallback, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Link2, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";

// Resize + compress a File into a JPEG data URL
const fileToCompressedDataUrl = (file, { maxSize = 1200, quality = 0.82 } = {}) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Not a valid image"));
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff"; // in case original is PNG w/ transparency
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const out = canvas.toDataURL("image/jpeg", quality);
        resolve(out);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const humanSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const approxSizeOfDataUrl = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return 0;
  const idx = dataUrl.indexOf(",");
  if (idx < 0) return 0;
  return Math.round((dataUrl.length - idx - 1) * 0.75);
};

/**
 * ImageInput
 * - Accepts a URL string OR a base64 data URL
 * - Supports paste, drag & drop, file picker
 * - Auto compresses uploads to ~1200px JPEG at ~82% quality
 */
const ImageInput = ({ value, onChange, height = "h-24" }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const isDataUrl = typeof value === "string" && value.startsWith("data:");
  const sizeBytes = isDataUrl ? approxSizeOfDataUrl(value) : 0;

  const handleFiles = useCallback(async (files) => {
    if (!files || !files.length) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast({ title: "Not an image", description: "Please choose an image file." });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "Too large", description: "Please choose an image under 15 MB." });
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      onChange(dataUrl);
      const b = approxSizeOfDataUrl(dataUrl);
      toast({ title: "Image ready", description: `Compressed to ${humanSize(b)} · embedded in site.json` });
    } catch (e) {
      toast({ title: "Could not process image", description: e.message });
    } finally {
      setBusy(false);
    }
  }, [onChange, toast]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFiles(e.dataTransfer?.files);
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === "file") {
        e.preventDefault();
        handleFiles([it.getAsFile()]);
        return;
      }
    }
  };

  return (
    <div>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onPaste={onPaste}
        placeholder="Paste image URL or drag & drop a file below…"
        className="h-10 bg-cream border-[#e3d3ad]"
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`mt-2 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors px-3 py-4 text-[13px] ${drag ? "border-brand bg-[#f9f1de]" : "border-[#e3d3ad] hover:border-brand hover:bg-[#faf3e2]"}`}
      >
        {busy ? (
          <><Loader2 className="h-4 w-4 animate-spin text-brand" /> Processing image…</>
        ) : (
          <>
            <Upload className="h-4 w-4 text-brand" />
            <span className="text-[#3a2a20]">Drag & drop an image here, or click to browse</span>
            <span className="text-[11.5px] text-[#a08774] hidden md:inline">· auto-resized to 1200px JPEG</span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {value && (
        <div className="mt-2 flex items-start gap-3">
          <img src={value} alt="" className={`${height} rounded-lg object-cover border border-[#efe4c9]`} />
          <div className="min-w-0 text-[12px] text-[#6b4f39]">
            <div className="flex items-center gap-1.5 font-semibold text-[#2a1a15]">
              {isDataUrl ? <ImageIcon className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
              {isDataUrl ? `Embedded (${humanSize(sizeBytes)})` : "External URL"}
            </div>
            <div className="truncate max-w-[280px] mt-0.5">
              {isDataUrl ? "Stored directly inside site.json — no external hosting needed." : value}
            </div>
            <button type="button" onClick={() => onChange("")} className="mt-1.5 inline-flex items-center gap-1 text-brand hover:underline">
              <X className="h-3 w-3" /> Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageInput;
